import { useEffect } from 'react';
import { Controller } from 'react-hook-form';

import {
  ComponentSettingNumber,
  NumberConversionType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import Markdown from 'dashboard/components/Markdown';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import Input from 'design-system/components/Input';
import { Label, Paragraph } from 'design-system/components/Text';
import { UiComponentConfig } from 'lib/interfaces/ui-components';
import { useFormSecurely } from 'lib/security/reactHookForm';

const generateConverters = (conversionType: NumberConversionType | null) => {
  let displayToValueConverter: (num: number) => number;
  let valueToDisplayConverter: (num: number) => number;
  switch (conversionType) {
    case NumberConversionType.BytesToMb:
      valueToDisplayConverter = (bytes: number): number => bytes / 1024 / 1024;
      displayToValueConverter = (mb: number): number => mb * 1024 * 1024;
      break;
    default:
      valueToDisplayConverter = (num: number) => num;
      displayToValueConverter = (num: number) => num;
      break;
  }
  return {
    valueToDisplayConverter,
    displayToValueConverter,
  };
};

interface NumberSettingProps {
  editable: boolean;
  componentSetting: ComponentSettingNumber;
  componentConfig: UiComponentConfig['config'];
  handleUpdateComponentConfig: (componentConfigUpdates: Record<string, any>) => void;
}

const NumberSetting = ({
  editable,
  componentSetting,
  componentConfig,
  handleUpdateComponentConfig,
}: NumberSettingProps): JSX.Element => {
  const {
    'client-key': clientKey,
    'helper-text': helperText,
    min,
    max,
    conversion,
    'default-value': defaultValue,
  } = componentSetting;

  const { displayToValueConverter, valueToDisplayConverter } = generateConverters(conversion);

  const initialValue =
    componentConfig?.[clientKey as keyof UiComponentConfig['config']] || defaultValue;

  const { control, handleSubmit, reset } = useFormSecurely({
    defaultValues: {
      [clientKey]: initialValue,
    },
  });

  const handleSubmitForm = handleSubmit((data) => {
    handleUpdateComponentConfig({ [clientKey]: data[clientKey] });
  });

  useEffect(() => reset({ [clientKey]: initialValue }), [initialValue, reset, clientKey]);

  return (
    <>
      <Label weight='bold'>
        {componentSetting.label}
        {helperText && (
          <HelpIcon>
            <Paragraph size='sm'>
              <Markdown>{helperText}</Markdown>
            </Paragraph>
          </HelpIcon>
        )}
      </Label>
      <Controller
        name={clientKey}
        control={control}
        render={({ field: { value, onChange } }) => (
          <Input
            id={`form-section-number-input-setting__${clientKey}`}
            aria-label={`${clientKey}-number-input`}
            type='number'
            disabled={!editable}
            value={typeof value === 'string' ? value : valueToDisplayConverter(value)}
            onBlur={(value) => {
              const numberValue = parseInt(value.target.value);
              const convertedValue = isNaN(numberValue)
                ? defaultValue
                : displayToValueConverter(numberValue);

              // only apply restriction on blur so that users can freely type on input
              if (min && convertedValue < min) {
                onChange(min);
              } else if (max && convertedValue > max) {
                onChange(max);
              } else {
                onChange(convertedValue);
              }
              handleSubmitForm();
            }}
            onChange={(value) => {
              const numberValue = parseInt(value.target.value);
              // temporarily set to empty string if user clears text
              // we can't use undefined here since this will cause the form to reset
              // to the initial value
              onChange(isNaN(numberValue) ? '' : displayToValueConverter(numberValue));
            }}
          />
        )}
      />
    </>
  );
};

export default NumberSetting;
